import { Send, Trash2, X } from 'lucide-react';
import {
  FC, KeyboardEvent, ReactElement, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { useOrganizationState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';

import stefanAvatar from '../../../../assets/stefan.jpg';
import { axiosInstance } from '../../../../axios';
import { useWarmup } from '../../../../hooks/useWarmup';
import { StefanKbNotifier } from './StefanKbNotifier';

/**
 * Chat z asystentem AI panelu Hotelspot — osadzony bezpośrednio na Dashboardzie
 * (zastąpił sekcję "Co nowego"). Kompaktowy layout dopasowany do prawej kolumny
 * grida 70/30 — wąski, wysoki, z mniejszymi paddingami niż wersja drawerowa.
 *
 * Backend: `POST /v3-panelHelpChat` (codebase `ai`) z `{ message, history, organizationId, language }`.
 */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface ApiResponse {
  success: boolean;
  data?: { answer: string };
  error?: string;
}

/* ============================================================
   Styles — kompaktowa wersja
   ============================================================ */

const Card = styled.div`
  /* Sztywna wysokość — nie rozciąga się z sąsiednim kafelkiem w gridzie.
     align-self: start dodatkowo zabezpiecza przed stretch w grid layout. */
  display: flex;
  flex-direction: column;
  height: 460px;
  align-self: start;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#e5e7eb')};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${({ theme }) => (theme.mode === 'dark'
    ? '0 1px 3px rgba(0, 0, 0, 0.3)'
    : '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.03)')};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#eef0f3')};
`;

/* Avatar Stefana — okrągły, ze zdjęciem zamiast ikonki. */
const HeaderAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f1f5f9;
  border: 2px solid ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  gap: 1px;
`;

/* Wrap dla tytułu + BETA badge w jednej linii. */
const HeaderTitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const HeaderTitle = styled.div`
  font-size: 13.5px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.2;
  letter-spacing: -0.1px;
`;

/* Badge "BETA" — kapsułka w stylu produktowym (Notion / Linear).
   Subtelny gradient + uppercase + tracking. Sygnalizuje że asystent jest
   w fazie testowej, ale wygląda profesjonalnie. */
const BetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #ffffff;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  line-height: 1.4;
  box-shadow: 0 1px 2px rgba(217, 119, 6, 0.3);
  flex-shrink: 0;
`;

/* Przycisk wyczyść czat — ikona w headerze. */
const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#e5e7eb')};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#fef2f2')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#fecaca')};
    color: #dc2626;
  }

  svg { width: 14px; height: 14px; }
`;

/* Zamknięcie panelu — ikona X po prawej stronie headera. Bez cienia i bez
   zaokrągleń (border-radius: 0), zgodnie z życzeniem. */
const HeaderCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#e5e7eb')};
  border-radius: 0;
  box-shadow: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#f3f4f6')};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg { width: 15px; height: 15px; }
`;

const HeaderHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.3;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
    flex-shrink: 0;
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0a0e13' : '#fafbfc')};

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)')};
  }
`;

/* Wiersz wiadomości — dla assistant pokazuje avatar po lewej obok bubble. */
const MessageRow = styled.div<{ $role: 'user' | 'assistant' }>`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  max-width: 100%;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  flex-direction: ${({ $role }) => ($role === 'user' ? 'row-reverse' : 'row')};
`;

/* Mały awatar Stefana przy wiadomościach assistant. */
const MessageAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  display: block;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.15);
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 88%;
  padding: 10px 13px;
  border-radius: 14px;
  font-size: 12.5px;
  line-height: 1.55;
  word-wrap: break-word;
  letter-spacing: 0.05px;

  background: ${({ $role, theme }) => {
    if ($role === 'user') return 'linear-gradient(135deg, #0a7465 0%, #0d8a78 100%)';
    return theme.mode === 'dark' ? '#1a2028' : '#ffffff';
  }};
  color: ${({ $role, theme }) => ($role === 'user' ? '#ffffff' : theme.colors.text.primary)};
  border: ${({ $role, theme }) => ($role === 'user'
    ? 'none'
    : `1px solid ${theme.mode === 'dark' ? '#1f2937' : '#eef0f3'}`)};
  box-shadow: ${({ $role }) => ($role === 'user'
    ? '0 2px 8px rgba(10, 116, 101, 0.18)'
    : '0 1px 2px rgba(0, 0, 0, 0.03)')};
  border-${({ $role }) => ($role === 'user' ? 'bottom-right' : 'bottom-left')}-radius: 5px;

  strong { font-weight: 400; }

  a {
    color: ${({ $role }) => ($role === 'user' ? '#ffffff' : '#0a7465')};
    font-weight: 300;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.75;
    }
  }

  code {
    font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 11px;
    padding: 2px 5px;
    border-radius: 4px;
    background: ${({ $role, theme }) => {
    if ($role === 'user') return 'rgba(255,255,255,0.18)';
    return theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15, 23, 42, 0.06)';
  }};
  }

  ol, ul {
    margin: 6px 0;
    padding-left: 18px;
  }
  li { margin: 2px 0; }
  p { margin: 0; }
  p + p { margin-top: 6px; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1;
  padding: 20px 14px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  background: #f1f5f9;
  border: 2px solid ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
  margin-bottom: 4px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.18);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const EmptyTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.primary};
  letter-spacing: -0.1px;
`;

const EmptyText = styled.div`
  font-size: 11.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 260px;
`;

const Examples = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
  width: 100%;
`;

const ExampleChip = styled.button`
  text-align: left;
  font-size: 11.5px;
  font-weight: 500;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#ffffff')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: all 0.15s ease;
  position: relative;
  padding-left: 28px;

  /* Mała ikona "→" przed tekstem */
  &::before {
    content: '→';
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #0a7465;
    font-weight: 400;
    transition: transform 0.15s ease;
  }

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(10, 116, 101, 0.04)')};
    border-color: rgba(10, 116, 101, 0.4);

    &::before {
      transform: translate(2px, -50%);
    }
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-2px); }
`;

const TypingDots = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)')};
    animation: ${dotPulse} 1.2s infinite ease-in-out;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;

const Composer = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#eef0f3')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 12.5px;
  font-family: inherit;
  line-height: 1.45;
  max-height: 88px;
  min-height: 36px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0a0e13' : '#fafbfc')};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.secondary};
    opacity: 0.6;
  }

  &:focus {
    border-color: #0a7465;
    background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};
    box-shadow: 0 0 0 3px rgba(10, 116, 101, 0.12);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #0a7465 0%, #0d8a78 100%);
  color: #ffffff;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  box-shadow: 0 2px 6px rgba(10, 116, 101, 0.25);

  &:hover:not(:disabled) {
    box-shadow: 0 4px 10px rgba(10, 116, 101, 0.35);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(10, 116, 101, 0.25);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    box-shadow: none;
  }

  svg { width: 14px; height: 14px; }
`;

/* ============================================================
   Markdown rendering — wspiera **bold**, `code`, [text](url), listy
   ============================================================ */

/**
 * Mapa ścieżka → ludzka nazwa. Używana w preprocessorze defensywnym do
 * konwersji "gołych" URL (np. /events/new bez markdown) na klikalne linki.
 * Lista pochodzi z menuConfig — keep in sync gdy panel zmieni routing.
 */
const PATH_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/analytics': 'Analityka',
  '/events': 'Wydarzenia',
  '/events/all-events': 'Wydarzenia',
  '/events/new': 'Utwórz wydarzenie',
  '/events/categories': 'Kategorie wydarzeń',
  '/events/register': 'Rejestracje na wydarzenia',
  '/special-offer': 'Oferty specjalne',
  '/special-offer/new': 'Dodaj ofertę',
  '/special-offer/categories': 'Kategorie ofert',
  '/spa': 'SPA',
  '/room-service': 'Room service',
  '/products-services': 'Sklep',
  '/product-rental': 'Wypożyczalnia',
  '/product-rental-reservations': 'Rezerwacje wypożyczalni',
  '/conferences': 'Konferencje',
  '/museum': 'Muzeum',
  '/orders': 'Zamówienia',
  '/order-products': 'Zamówienia sklepu',
  '/reports': 'Zgłoszenia',
  '/alerts': 'Alerty',
  '/marketing': 'Marketing',
  '/promo-codes': 'Kody rabatowe',
  '/welcome-promo-codes': 'Kody powitalne',
  '/subscribers': 'Subskrybenci',
  '/surveys': 'Ankiety',
  '/settings': 'Ustawienia',
  '/settings#general': 'Ogólne',
  '/settings#team': 'Zespół',
  '/settings#mobile-app': 'Aplikacja mobilna',
  '/settings#housekeeping': 'Housekeeping',
  '/settings#web-qr': 'Aplikacja webowa',
  '/settings#devices': 'Moje urządzenia',
  '/settings#danger': 'Strefa zagrożenia',
  '/my-profile': 'Mój profil',
  '/my-profile#profile': 'Dane profilowe',
  '/my-profile#account-info': 'Informacje o koncie',
  '/my-profile#security': 'Bezpieczeństwo',
  '/my-profile#language': 'Język aplikacji',
  '/my-profile#notifications': 'Powiadomienia',
  '/change-plan': 'Zmiana planu',
  '/faq-help': 'FAQ administratora',
  '/support-service': 'Wsparcie',
  '/hotel': 'Hotel',
  '/hotel/general-information': 'Informacje ogólne',
  '/hotel/amenities': 'Udogodnienia',
  '/hotel/business-card': 'Wizytówki',
  '/hotel/good-practice': 'Dobre praktyki',
  '/hotel/phonebook': 'Książka telefoniczna',
  '/hotel/wifi': 'Wi-Fi',
  '/hotel/regulations': 'Regulaminy',
  '/hotel/poi': 'Atrakcje',
  '/hotel/tv-chanel': 'Kanały TV',
  '/hotel/faq': 'FAQ dla gości',
  '/hotel/loyalty-program': 'Program lojalnościowy',
  '/hotel/social-media': 'Social media',
  '/hotel/virtual-tour': 'Wirtualny spacer',
  '/hotel/link-to-reviews': 'Linki do opinii',
  '/hotel/custom-blocks': 'Custom Blocks',
};

/**
 * Defensywny preprocessor: czasami AI mimo system promptu zwraca URL bez
 * markdownu (np. w backtickach \`/events/new\` lub goły /events/new w tekście).
 * Ta funkcja wykrywa takie wystąpienia i ZAMIENIA je na pełen markdown link
 * `[Nazwa](/path)`, używając PATH_LABELS jako źródła nazwy.
 *
 * Kolejność:
 * 1. URL w backtickach: \`/events/new\` → [Utwórz wydarzenie](/events/new)
 * 2. URL goły (poza już istniejącym markdownem): /events/new → [...](...)
 *    - Pomijamy ścieżki, które są już w (...) markdown linku
 */
const ensureLinksFormatted = (text: string): string => {
  let out = text;

  // Krok 1: URL w backtickach (np. `/events/new` lub `/settings#team`)
  out = out.replace(/`(\/[a-z0-9/#-]+)`/gi, (_match, path: string) => {
    const label = PATH_LABELS[path];
    if (label) return `[${label}](${path})`;
    return `[${path}](${path})`;
  });

  // Krok 2: URL goły. Używamy negative lookbehind przez zastąpienie po
  // tymczasowym maskowaniu istniejących markdown linków.
  // Maskujemy `[...](...)` zanim szukamy gołych URL.
  const masks: string[] = [];
  out = out.replace(/\[[^\]]+\]\([^)]+\)/g, (m) => {
    masks.push(m);
    return ` ${masks.length - 1} `;
  });

  // Teraz szukaj gołych URL (zaczynają się od /, kończą przed znakiem niedopasowanym).
  // Wspieramy fragment URL (#section) — `[a-z0-9/#-]*`.
  out = out.replace(/(^|[\s(])(\/[a-z0-9][a-z0-9/#-]*)(?=[\s.,!?)]|$)/gi, (_match, prefix: string, path: string) => {
    const label = PATH_LABELS[path];
    if (label) return `${prefix}[${label}](${path})`;
    // Nieznana ścieżka — zostaw jak jest (nie linkujemy zmyślonych).
    return `${prefix}${path}`;
  });

  // Odmaskuj.
  out = out.replace(/ (\d+) /g, (_m, idx: string) => masks[parseInt(idx, 10)] || '');

  return out;
};

/**
 * Linki w odpowiedziach AI: oznaczamy je atrybutem `data-internal-link="<path>"`
 * (dla wewnętrznych) lub zwykłym `target="_blank"` (dla zewnętrznych).
 * Click jest obsługiwany przez delegowany handler na kontenerze Messages —
 * eliminuje to problemy z closure / event bubbling przez dynamiczny content.
 */
const renderInline = (s: string): Array<string | ReactElement> => {
  const parts: Array<string | ReactElement> = [];
  let key = 0;
  const re = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0;
  let m: RegExpExecArray | null = re.exec(s);
  while (m) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    if (m[2] && m[3]) {
      const text = m[2];
      const url = m[3];
      const isInternal = url.startsWith('/');
      if (isInternal) {
        parts.push(
          <a
            key={`l-${key++}`}
            href={url}
            data-internal-link={url}
          >
            {text}
          </a>,
        );
      } else {
        parts.push(
          <a
            key={`l-${key++}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text}
          </a>,
        );
      }
    } else if (m[4]) {
      parts.push(<strong key={`b-${key++}`}>{m[4]}</strong>);
    } else if (m[5]) {
      parts.push(<code key={`c-${key++}`}>{m[5]}</code>);
    }
    last = re.lastIndex;
    m = re.exec(s);
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
};

const renderMarkdown = (rawText: string): ReactElement[] => {
  // Defensywnie przetwarzamy tekst — zamieniamy "gołe" URL na pełne markdown
  // linki, żeby AI nie musiało być idealne.
  const text = ensureLinksFormatted(rawText);
  const blocks = text.split(/\n\n+/);
  return blocks.map((block, i) => {
    const lines = block.split('\n');
    const isOrdered = lines.every((l) => /^\d+\.\s/.test(l.trim()));
    const isUnordered = lines.every((l) => /^[-*]\s/.test(l.trim()));

    if (isOrdered && lines.length > 0) {
      return (
        <ol key={i}>
          {lines.map((l, j) => (
            <li key={j}>{renderInline(l.replace(/^\d+\.\s/, ''))}</li>
          ))}
        </ol>
      );
    }
    if (isUnordered && lines.length > 0) {
      return (
        <ul key={i}>
          {lines.map((l, j) => (
            <li key={j}>{renderInline(l.replace(/^[-*]\s/, ''))}</li>
          ))}
        </ul>
      );
    }
    return <p key={i}>{renderInline(block)}</p>;
  });
};

/* ============================================================
   Component
   ============================================================ */

// Przykładowe pytania na pustym czacie — przez `t()`, bo to tekst widoczny
// dla usera (wcześniej były zaszyte po polsku niezależnie od języka panelu).
const SUGGESTED_QUESTION_KEYS = [
  { key: 'helpAiChat.suggested1', fallback: 'Jak dodać wydarzenie?' },
  { key: 'helpAiChat.suggested2', fallback: 'Gdzie dodam ofertę specjalną?' },
  { key: 'helpAiChat.suggested3', fallback: 'Jak skonfigurować SPA?' },
];

// Persystencja historii konwersacji Stefana w localStorage.
// Klucz scopowany per-organizacja, żeby przełączenie hotelu nie pokazywało
// poprzedniej historii. Limit 100 ostatnich wiadomości — nie rośnie bez końca.
const STEFAN_HISTORY_KEY_PREFIX = 'stefan-chat-history';
const HISTORY_MAX_MESSAGES = 100;

const getHistoryKey = (orgId: string | null | undefined): string => `${STEFAN_HISTORY_KEY_PREFIX}:${orgId || 'default'}`;

const loadHistoryFromStorage = (orgId: string | null | undefined): ChatMessage[] => {
  try {
    const raw = localStorage.getItem(getHistoryKey(orgId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is ChatMessage => typeof m === 'object'
        && m !== null
        && typeof m.id === 'string'
        && (m.role === 'user' || m.role === 'assistant')
        && typeof m.content === 'string',
    );
  } catch {
    return [];
  }
};

const saveHistoryToStorage = (orgId: string | null | undefined, messages: ChatMessage[]): void => {
  try {
    const trimmed = messages.slice(-HISTORY_MAX_MESSAGES);
    localStorage.setItem(getHistoryKey(orgId), JSON.stringify(trimmed));
  } catch {
    // localStorage może być pełny / zablokowany — ignorujemy.
  }
};

interface HelpAiChatProps {
  /** Gdy podane — w headerze pojawia się guzik X zamykający panel. */
  onClose?: () => void;
}

export const HelpAiChat: FC<HelpAiChatProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const { organizationId } = useOrganizationState();
  const navigate = useLocaleNavigate();

  // Czat siedzi na Dashboardzie (mount = wejście na pulpit), więc grzejemy
  // instancję od razu — zanim hotelarz zdąży napisać pierwsze pytanie.
  useWarmup('v3-panelHelpChat');

  const [messages, setMessages] = useState<ChatMessage[]>(() => loadHistoryFromStorage(organizationId));
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Przy zmianie organizacji (przełączenie hotelu) — załaduj historię nowej org.
  useEffect(() => {
    setMessages(loadHistoryFromStorage(organizationId));
  }, [organizationId]);

  // Persystuj historię przy każdej zmianie.
  useEffect(() => {
    saveHistoryToStorage(organizationId, messages);
  }, [organizationId, messages]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Auto-grow textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 88)}px`;
  }, [input]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;

    setError(null);
    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      // Backend `panelHelpChat` ma limit historii 20 wiadomości — wysyłamy
      // tylko ostatnie 20. Pełna historia (do 100) jest trzymana lokalnie
      // dla UX (gość widzi cały kontekst), ale do LLM idzie skrócony zestaw.
      const recentHistory = messages.slice(-20);
      const res = await axiosInstance.post<ApiResponse>('/v3-panelHelpChat', {
        message: trimmed,
        history: recentHistory.map((m) => ({ role: m.role, content: m.content })),
        organizationId: organizationId || undefined,
        language: (i18n.language || 'pl').split('-')[0],
      });

      if (!res.data.success || !res.data.data?.answer) {
        throw new Error(res.data.error || 'Empty response');
      }

      const assistantMsg: ChatMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: res.data.data.answer,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      setError(t('helpAiChat.errorAnswer', 'Nie udało się uzyskać odpowiedzi. Spróbuj ponownie.'));
      // eslint-disable-next-line no-console
      console.error('[HelpAiChat] send error', message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  /** Czyści historię czatu — w pamięci i w localStorage. */
  const clearChat = () => {
    setMessages([]);
    setError(null);
    setInput('');
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(getHistoryKey(organizationId));
      } catch {
        // ignored — np. Safari private mode
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Delegowany click handler — łapie kliknięcia w dowolnego anchora wewnątrz
  // bańki czatu i nawiguje routerem zamiast pełnym page reload. Korzystamy z
  // `data-internal-link` ustawianego w renderze markdown (link wewnętrzny =
  // ścieżka z `/` na początku). External links (target=_blank) nie mają tego
  // atrybutu i przelatują przez handler bez zmian.
  const handleMessagesClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a[data-internal-link]') as HTMLAnchorElement | null;
    if (!anchor) return;
    const path = anchor.getAttribute('data-internal-link');
    if (!path) return;
    e.preventDefault();
    navigate(path);
  };

  return (
    <Card>
      <Header>
        <HeaderAvatar>
          <img src={stefanAvatar} alt="Stefan" />
        </HeaderAvatar>
        <HeaderText>
          <HeaderTitleRow>
            <HeaderTitle>{t('helpAiChat.title', 'Stefan — Asystent AI')}</HeaderTitle>
            <BetaBadge>{t('helpAiChat.beta', 'Beta')}</BetaBadge>
          </HeaderTitleRow>
          <HeaderHint>{t('helpAiChat.online', 'Dostępny — odpowiada od razu')}</HeaderHint>
        </HeaderText>
        {messages.length > 0 && (
          <ClearButton
            type="button"
            onClick={clearChat}
            aria-label={t('helpAiChat.clearChat', 'Wyczyść czat')}
            title={t('helpAiChat.clearChat', 'Wyczyść czat')}
          >
            <Trash2 />
          </ClearButton>
        )}
        {onClose && (
          <HeaderCloseButton
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Zamknij')}
            title={t('common.close', 'Zamknij')}
          >
            <X />
          </HeaderCloseButton>
        )}
      </Header>

      {/* Banner „agent chce się czegoś nauczyć" — pod headerem Stefana, nad
          historią czatu. Renderuje się tylko gdy są dirty sekcje KB. */}
      <StefanKbNotifier variant="inline" />

      <Messages ref={scrollRef} onClick={handleMessagesClick}>
        {messages.length === 0 && !isLoading && (
          <EmptyState>
            <EmptyIcon>
              <img src={stefanAvatar} alt="Stefan" />
            </EmptyIcon>
            <EmptyTitle>{t('helpAiChat.emptyTitle', 'W czym mogę Ci pomóc?')}</EmptyTitle>
            <EmptyText>
              {t('helpAiChat.emptyText', 'Zadaj pytanie — odpowiem i wskażę gdzie kliknąć.')}
            </EmptyText>
            <Examples>
              {SUGGESTED_QUESTION_KEYS.map(({ key, fallback }) => {
                const question = t(key, fallback);
                return (
                  <ExampleChip key={key} type="button" onClick={() => sendMessage(question)}>
                    {question}
                  </ExampleChip>
                );
              })}
            </Examples>
          </EmptyState>
        )}

        {messages.map((m) => (
          <MessageRow key={m.id} $role={m.role}>
            {m.role === 'assistant' && <MessageAvatar src={stefanAvatar} alt="Stefan" />}
            <Bubble $role={m.role}>
              {renderMarkdown(m.content)}
            </Bubble>
          </MessageRow>
        ))}

        {isLoading && (
          <MessageRow $role="assistant">
            <MessageAvatar src={stefanAvatar} alt="Stefan" />
            <Bubble $role="assistant">
              <TypingDots>
                <span />
                <span />
                <span />
              </TypingDots>
            </Bubble>
          </MessageRow>
        )}

        {error && (
          <MessageRow $role="assistant">
            <MessageAvatar src={stefanAvatar} alt="Stefan" />
            <Bubble $role="assistant" style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626' }}>
              {error}
            </Bubble>
          </MessageRow>
        )}
      </Messages>

      <Composer onSubmit={handleSubmit}>
        <TextArea
          ref={textareaRef}
          placeholder={t('helpAiChat.placeholder', 'Napisz pytanie…')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          rows={1}
        />
        <SendButton type="submit" disabled={isLoading || input.trim().length === 0} aria-label={t('helpAiChat.send', 'Wyślij')}>
          <Send />
        </SendButton>
      </Composer>
    </Card>
  );
};
